import React, { useState, useEffect } from 'react';
import ToggleSwitch from './ToggleSwitch';
import { useDarkMode, ThemeMode } from '../../contexts/DarkModeContext';
import { Sun, Moon, Monitor, FileText, Smile, PartyPopper, Mail } from 'lucide-react';

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  emojiLevel: 'none' | 'medium' | 'many';
  studySettings: {
    autoPlayAudio: boolean;
    cardAnimations: boolean;
  };
  emailNotifications: {
    streakReminders: boolean;
    studyTips: boolean;
  };
}

interface PreferencesSectionProps {
  preferences: UserPreferences;
  onPreferencesUpdate: (preferences: Partial<UserPreferences>) => Promise<void>;
}

const PreferencesSection: React.FC<PreferencesSectionProps> = ({
  preferences,
  onPreferencesUpdate
}) => {
  const { themeMode, setThemeMode } = useDarkMode();
  const [isLoading, setIsLoading] = useState<string | null>(null);

  // Sync theme mode with preferences
  useEffect(() => {
    if (preferences.theme !== themeMode) {
      setThemeMode(preferences.theme as ThemeMode);
    }
  }, [preferences.theme, themeMode, setThemeMode]);

  const handlePreferenceChange = async (
    key: keyof UserPreferences,
    value: any,
    subKey?: string
  ) => {
    setIsLoading(`${key}${subKey ? `.${subKey}` : ''}`);

    try {
      let updateData: Partial<UserPreferences>;

      if (subKey && typeof preferences[key] === 'object') {
        updateData = {
          [key]: {
            ...(preferences[key] as any),
            [subKey]: value
          }
        } as Partial<UserPreferences>;
      } else {
        updateData = { [key]: value } as Partial<UserPreferences>;
      }

      // Apply theme change immediately for better UX
      if (key === 'theme') {
        setThemeMode(value as ThemeMode);
      }

      await onPreferencesUpdate(updateData);
    } catch (error) {
      console.error('Error updating preference:', error);
      // Revert theme change if save failed
      if (key === 'theme') {
        setThemeMode(preferences.theme as ThemeMode);
      }
      // The error will be handled by the parent component
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Appearance & AI Section */}
      <div>
        <h3 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-4">
          Vzhled a AI
        </h3>
        <div className="space-y-6">
          {/* Theme Selector */}
          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
              Barevný režim
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'light', label: 'Světlý', icon: <Sun className="w-5 h-5" /> },
                { value: 'dark', label: 'Tmavý', icon: <Moon className="w-5 h-5" /> },
                { value: 'system', label: 'Systém', icon: <Monitor className="w-5 h-5" /> }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handlePreferenceChange('theme', option.value)}
                  disabled={isLoading === 'theme'}
                  className={`
                    p-3 rounded-2xl border-2 text-sm font-bold transition-all duration-200
                    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800
                    ${themeMode === option.value
                      ? 'border-primary bg-primary/10 dark:bg-primary/20 text-amber-700 dark:text-amber-300 border-b-4 border-b-primary'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:-translate-y-0.5'
                    }
                    ${isLoading === 'theme' ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <div className="flex flex-col items-center space-y-1.5">
                    {option.icon}
                    <span>{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Emoji Level Selector */}
          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
              Úroveň emoji v odpovědích AI tutora
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'none', label: 'Žádné', icon: <FileText className="w-5 h-5" /> },
                { value: 'medium', label: 'Střední', icon: <Smile className="w-5 h-5" /> },
                { value: 'many', label: 'Hodně', icon: <PartyPopper className="w-5 h-5" /> }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handlePreferenceChange('emojiLevel', option.value)}
                  disabled={isLoading === 'emojiLevel'}
                  className={`
                    p-3 rounded-2xl border-2 text-sm font-bold transition-all duration-200
                    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800
                    ${preferences.emojiLevel === option.value
                      ? 'border-primary bg-primary/10 dark:bg-primary/20 text-amber-700 dark:text-amber-300 border-b-4 border-b-primary'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:-translate-y-0.5'
                    }
                    ${isLoading === 'emojiLevel' ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <div className="flex flex-col items-center space-y-1.5">
                    {option.icon}
                    <span>{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Study Settings */}
      <div>
        <h3 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-4">
          Nastavení studia
        </h3>
        <div className="space-y-4">
          <ToggleSwitch
            id="autoPlayAudio"
            label="Automatické přehrávání audia"
            description="Automaticky přehrávat audio při otevření audioknih"
            checked={preferences.studySettings.autoPlayAudio}
            onChange={(checked) => handlePreferenceChange('studySettings', checked, 'autoPlayAudio')}
            disabled={isLoading === 'studySettings.autoPlayAudio'}
          />

          <ToggleSwitch
            id="cardAnimations"
            label="Animace karet"
            description="Povolit animace při otáčení flashkaret"
            checked={preferences.studySettings.cardAnimations}
            onChange={(checked) => handlePreferenceChange('studySettings', checked, 'cardAnimations')}
            disabled={isLoading === 'studySettings.cardAnimations'}
          />
        </div>
      </div>

      {/* Email Notifications */}
      <div>
        <h3 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          E-mailová oznámení
        </h3>
        <div className="space-y-4">
          <ToggleSwitch
            id="streakReminders"
            label="Připomínka streak"
            description="Upozornění když vám hrozí ztráta streak"
            checked={preferences.emailNotifications?.streakReminders ?? true}
            onChange={(checked) => handlePreferenceChange('emailNotifications', checked, 'streakReminders')}
            disabled={isLoading === 'emailNotifications.streakReminders'}
          />

          <ToggleSwitch
            id="studyTips"
            label="Tipy a zpětná vazba"
            description="Občasné tipy ke studiu a dotazy na zpětnou vazbu"
            checked={preferences.emailNotifications?.studyTips ?? true}
            onChange={(checked) => handlePreferenceChange('emailNotifications', checked, 'studyTips')}
            disabled={isLoading === 'emailNotifications.studyTips'}
          />
        </div>
      </div>
    </div>
  );
};

export default PreferencesSection;